import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  RiPlayCircleLine,
  RiPauseLine,
  RiSkipBackLine,
  RiSkipForwardLine,
  RiRefreshLine,
  RiCheckLine,
  RiLoader4Line,
  RiNodeTree,
  RiFlowChart,
  RiCodeSSlashLine,
} from 'react-icons/ri';
import { useInjectCSS } from './tutorialHelpers';

/* ─── Extended CSS for workflow tutorial animations ─────────── */

const WF_CSS = `
@keyframes wftut-pop {
  from { opacity: 0; transform: scale(0.82); }
  to   { opacity: 1; transform: scale(1); }
}
@keyframes wftut-fadein {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes wftut-pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.55; }
}
.wftut-pop    { animation: wftut-pop    0.32s ease-out both; }
.wftut-fadein { animation: wftut-fadein 0.4s  ease-out both; }
.wftut-pulse  { animation: wftut-pulse  1.4s  ease      infinite; }

/* Mobile: reflow the fixed-width diagrams into a vertical stack so nothing
   overflows the page and no sideways scrolling is needed. Desktop is
   untouched (these rules apply only at <=600px). */
@media (max-width: 600px) {
  /* Simulation: stack the node canvas above the Variables panel and let the
     card grow to fit the taller layout. */
  .wftut-viewport  { height: auto !important; }
  .wftut-demo-area { overflow: visible !important; }
  .wftut-demo-panel {
    flex-direction: column !important;
    height: auto !important;
    min-width: 0 !important;
  }
  .wftut-canvas {
    flex: 0 0 auto !important;
    width: 100% !important;
    min-width: 0 !important;
    min-height: 96px;
  }
  .wftut-ctxpanel {
    flex: 0 0 auto !important;
    width: 100% !important;
    min-height: 72px;
  }
  .wftut-mininode { min-width: 72px !important; }
  /* Variable-binding diagram: stack the nodes vertically; the connector
     arrows rotate to point downward. */
  .wftut-varbind-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .wftut-varbind-node  { flex: 0 0 auto !important; min-width: 0 !important; }
  .wftut-varbind-arrow { align-self: center; transform: rotate(90deg); }
}
`;

function useWfCSS() {
  useEffect(() => {
    const id = 'stackform-wf-tutorial-css';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id;
      el.textContent = WF_CSS;
      document.head.appendChild(el);
    }
  }, []);
}

/* ─── Node colours (match WorkflowNodeConfig) ───────────────── */

const C = {
  input:  '#22c55e',
  output: '#ef4444',
  agent:  '#3b82f6',
  tool:   '#f97316',
  logic:  '#a855f7',
};

/* ─── Simulation step narrations ────────────────────────────── */

const NARRATIONS = [
  '',
  'Drop an Input node. Set its variable_name (here: query). Users fill this in every time they run the workflow.',
  'Drop an Agent node and connect it from Input. Write {{query}} in the prompt template to inject the input value.',
  'Give the Agent an output_variable name — result. Every downstream node that needs this agent\'s output must use that exact name.',
  'Drop an Output node, connect it from Agent, set its variable_name = result. It matches the agent\'s output_variable. ✓',
  'Click ▶ Run. A dialog collects a value for each Input node before execution begins.',
  'Engine starts. Input node writes query = "What is quantum computing?" into the shared variable context.',
  'Agent node executes. It reads {{query}} from context, calls the AI model, and writes result to context.',
  '✓ Output node reads result from context. The workflow is complete.',
];

/* ─── Mini node card ─────────────────────────────────────────── */

function MiniNode({ type, label, subLabel, highlight, visible, pulse }) {
  if (!visible) {
    return <div className="wftut-mininode" style={{ minWidth: 100 }} />;
  }
  const color = C[type];
  const isActive = highlight === 'active';
  const isDone   = highlight === 'done';
  return (
    <div
      className={`wftut-mininode wftut-pop${pulse ? ' wftut-pulse' : ''}`}
      style={{
        minWidth: 100,
        border: `2px solid ${isActive ? color : isDone ? `${color}66` : 'var(--border-color, #ddd)'}`,
        borderRadius: 6,
        overflow: 'hidden',
        background: isActive ? `${color}12` : 'var(--bg-primary, #fff)',
        boxShadow: isActive ? `0 0 0 3px ${color}28` : 'none',
        transition: 'border-color 0.25s, box-shadow 0.25s, background 0.25s',
      }}
    >
      <div style={{
        background: isActive ? color : isDone ? `${color}88` : color,
        color: '#fff',
        padding: '2px 7px',
        fontSize: '0.62rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        opacity: isDone ? 0.7 : 1,
      }}>
        {type.toUpperCase()}
      </div>
      <div style={{ padding: '4px 7px' }}>
        <div style={{
          fontSize: '0.76rem',
          fontWeight: 600,
          color: '#222',
        }}>
          {label}
        </div>
        {subLabel && (
          <div style={{
            fontSize: '0.67rem',
            color: '#555',
            fontFamily: 'monospace',
            marginTop: 1,
          }}>
            {subLabel}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Arrow connector ────────────────────────────────────────── */

function Arrow({ visible, active }) {
  if (!visible) return <div style={{ width: 20 }} />;
  return (
    <div
      className="wftut-fadein"
      style={{
        color: active ? C.agent : '#999',
        fontSize: '1rem',
        flexShrink: 0,
        transition: 'color 0.25s',
        userSelect: 'none',
      }}
    >
      →
    </div>
  );
}

/* ─── Variables context panel ────────────────────────────────── */

function ContextPanel({ step }) {
  const showQuery  = step >= 6;
  const showResult = step >= 7;
  const done       = step >= 8;

  return (
    <div className="wftut-ctxpanel" style={{
      flex: '0 0 42%',
      border: '1px solid var(--border-color, #ddd)',
      borderRadius: 6,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      fontSize: '0.72rem',
    }}>
      {/* Header */}
      <div style={{
        background: 'var(--bg-secondary, #f5f5f5)',
        borderBottom: '1px solid var(--border-color, #ddd)',
        padding: '3px 8px',
        fontWeight: 700,
        color: '#777',
        fontSize: '0.62rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}>
        Variables Context
      </div>

      {/* Variables */}
      <div style={{ padding: '6px 8px', flex: 1, fontFamily: 'monospace' }}>
        {!showQuery && (
          <span style={{
            color: '#aaa',
            fontStyle: 'italic',
            fontFamily: 'sans-serif',
            fontSize: '0.72rem',
          }}>
            {step <= 4 ? 'Run the workflow to populate.' : 'Starting…'}
          </span>
        )}
        {showQuery && (
          <div
            className="wftut-fadein"
            style={{
              marginBottom: 5,
              padding: '3px 6px',
              borderRadius: 3,
              background: `${C.input}12`,
              borderLeft: `3px solid ${C.input}`,
            }}
          >
            <span style={{ color: C.input, fontWeight: 700 }}>query</span>
            <span style={{ color: '#888' }}> = </span>
            <span style={{ color: '#333', fontSize: '0.67rem' }}>
              "What is quantum computing?"
            </span>
          </div>
        )}
        {showResult && (
          <div
            className="wftut-fadein"
            style={{
              padding: '3px 6px',
              borderRadius: 3,
              background: `${C.agent}12`,
              borderLeft: `3px solid ${C.agent}`,
            }}
          >
            <span style={{ color: C.agent, fontWeight: 700 }}>result</span>
            <span style={{ color: '#888' }}> = </span>
            <span style={{ color: '#333', fontSize: '0.67rem' }}>
              "Quantum computing uses qubits…"
            </span>
          </div>
        )}
      </div>

      {/* Status footer */}
      <div style={{
        padding: '3px 8px',
        borderTop: '1px solid var(--border-color, #ddd)',
        background: 'var(--bg-secondary, #f5f5f5)',
        fontSize: '0.67rem',
        fontWeight: done ? 700 : 400,
        color: done ? C.input : 'var(--text-secondary, #999)',
        minHeight: 22,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      }}>
        {step <= 3 && 'Building…'}
        {step === 4 && 'Ready to run'}
        {step === 5 && 'Awaiting inputs…'}
        {step === 6 && (
          <>
            <RiLoader4Line size={11} className="sdemo-spin" /> Input → context
          </>
        )}
        {step === 7 && (
          <>
            <RiLoader4Line size={11} className="sdemo-spin" /> Agent running…
          </>
        )}
        {step >= 8 && (
          <>
            <RiCheckLine size={11} /> Completed
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Main demo panel ────────────────────────────────────────── */

function WorkflowDemoPanel({ step }) {
  const showInput  = step >= 1;
  const showArrow1 = step >= 2;
  const showAgent  = step >= 2;
  const showArrow2 = step >= 4;
  const showOutput = step >= 4;

  const inputHL  = step === 6 ? 'active' : step > 6  ? 'done' : null;
  const agentHL  = step === 7 ? 'active' : step > 7  ? 'done' : null;
  const outputHL = step === 8 ? 'active' : null;

  const matchStep = step === 3 || step === 4;

  const agentSub = step < 3
    ? 'prompt: {{query}}'
    : `prompt: {{query}} | out: result`;

  return (
    <div className="wftut-demo-panel" style={{
      display: 'flex',
      height: '100%',
      gap: 8,
      padding: '0.7rem',
      boxSizing: 'border-box',
    }}>
      {/* Canvas side */}
      <div className="wftut-canvas" style={{
        flex: '0 0 56%',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        position: 'relative',
      }}>
        {/* Idle placeholder */}
        {step === 0 && (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '0.83rem',
            fontStyle: 'italic',
            flexDirection: 'column',
            gap: 6,
          }}>
            <span>Press <strong style={{ color: 'inherit' }}>Start</strong> to start</span>
            <span style={{ fontSize: '0.74rem' }}>
              or use ‹ › to step through manually
            </span>
          </div>
        )}

        {/* Node flow */}
        {step > 0 && (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            flexWrap: 'nowrap',
          }}>
            <MiniNode
              type="input"
              label="Input"
              subLabel="var: query"
              highlight={inputHL}
              visible={showInput}
            />
            <Arrow visible={showArrow1} active={step === 7} />
            <MiniNode
              type="agent"
              label="Agent"
              subLabel={agentSub}
              highlight={agentHL}
              visible={showAgent}
            />
            <Arrow visible={showArrow2} active={step === 8} />
            <MiniNode
              type="output"
              label="Output"
              subLabel="var: result"
              highlight={outputHL}
              visible={showOutput}
            />
          </div>
        )}

        {/* Name-match callout at steps 3–4 */}
        {matchStep && (
          <div
            className="wftut-fadein"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              fontSize: '0.68rem',
              color: '#666',
            }}
          >
            <span style={{
              background: `${C.agent}18`,
              border: `1px solid ${C.agent}44`,
              color: C.agent,
              borderRadius: 4,
              padding: '1px 6px',
              fontFamily: 'monospace',
              fontWeight: 700,
            }}>
              output_variable: result
            </span>
            <span>must match</span>
            <span style={{
              background: `${C.output}18`,
              border: `1px solid ${C.output}44`,
              color: C.output,
              borderRadius: 4,
              padding: '1px 6px',
              fontFamily: 'monospace',
              fontWeight: 700,
            }}>
              variable_name: result
            </span>
          </div>
        )}

        {/* Run-dialog overlay */}
        {step === 5 && (
          <div
            className="wftut-fadein"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.28)',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{
              background: 'var(--bg-primary, #fff)',
              border: '1px solid var(--border-color, #ddd)',
              borderRadius: 8,
              padding: '10px 14px',
              minWidth: 170,
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            }}>
              <div style={{
                fontWeight: 700,
                fontSize: '0.78rem',
                marginBottom: 6,
                color: '#222',
              }}>
                Provide Inputs
              </div>
              <div style={{
                fontSize: '0.7rem',
                marginBottom: 3,
                color: '#444',
              }}>
                query
              </div>
              <div style={{
                background: '#f0f0f0',
                border: '1px solid #d0d0d0',
                borderRadius: 4,
                padding: '3px 7px',
                fontFamily: 'monospace',
                fontSize: '0.68rem',
                color: '#333',
              }}>
                What is quantum computing?
              </div>
              <div style={{
                marginTop: 8,
                background: C.input,
                color: '#fff',
                borderRadius: 4,
                padding: '3px 0',
                textAlign: 'center',
                fontSize: '0.72rem',
                fontWeight: 700,
              }}>
                ▶ Run
              </div>
            </div>
          </div>
        )}

        {/* Step narration */}
        <div style={{
          minHeight: 52,
          fontSize: '0.74rem',
          color: '#333',
          lineHeight: 1.45,
          borderTop: '1px solid var(--border-color, #eee)',
          paddingTop: 5,
        }}>
          {NARRATIONS[step]}
        </div>
      </div>

      {/* Variables context panel */}
      <ContextPanel step={step} />
    </div>
  );
}

/* ─── Explainer helpers ──────────────────────────────────────── */

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: '0.71rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'var(--text-secondary, #888)',
      marginBottom: '0.65rem',
      marginTop: '1.1rem',
    }}>
      {children}
    </div>
  );
}

function NodeCard({ type, fields, description }) {
  const color = C[type];
  return (
    <div style={{
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'flex-start',
      marginBottom: '0.9rem',
    }}>
      <div style={{
        flexShrink: 0,
        background: color,
        color: '#fff',
        borderRadius: 5,
        padding: '3px 8px',
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        marginTop: 2,
        minWidth: 52,
        textAlign: 'center',
      }}>
        {type.toUpperCase()}
      </div>
      <div>
        <div style={{ fontSize: '0.84rem', marginBottom: 3, lineHeight: 1.4 }}>
          {description}
        </div>
        <div style={{
          fontSize: '0.76rem',
          color: 'var(--text-color)',
          fontFamily: 'monospace',
        }}>
          {fields.map((f, i) => (
            <span key={i} style={{ marginRight: 10 }}>
              <span style={{ color }}>{f.name}</span>
              <span style={{ color: 'var(--text-secondary, #aaa)' }}>
                {' — '}{f.desc}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ExplainerRow({ Icon, iconColor, label, description }) {
  return (
    <div style={{
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'flex-start',
      marginBottom: '0.85rem',
    }}>
      <span style={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        borderRadius: '7px',
        backgroundColor: `${iconColor}18`,
        color: iconColor,
        marginTop: 1,
      }}>
        <Icon size={16} />
      </span>
      <div>
        <div style={{
          fontWeight: 600,
          fontSize: '0.86rem',
          marginBottom: 2,
        }}>
          {label}
        </div>
        <div style={{
          fontSize: '0.82rem',
          color: 'var(--text-color)',
          lineHeight: 1.5,
        }}>
          {description}
        </div>
      </div>
    </div>
  );
}

/* ─── Variable binding diagram ───────────────────────────────── */

function VarBindingDiagram() {
  const tag = (color, text) => (
    <span style={{
      display: 'inline-block',
      padding: '1px 8px',
      borderRadius: 4,
      background: `${color}15`,
      border: `1px solid ${color}44`,
      color,
      fontFamily: 'monospace',
      fontSize: '0.75rem',
      fontWeight: 700,
    }}>
      {text}
    </span>
  );

  const NodeBox = ({ type, lines }) => (
    <div className="wftut-varbind-node" style={{
      border: `1px solid ${C[type]}66`,
      borderRadius: 6,
      overflow: 'hidden',
      minWidth: 110,
      flex: 1,
    }}>
      <div style={{
        background: C[type],
        color: '#fff',
        padding: '2px 8px',
        fontSize: '0.63rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
      }}>
        {type.toUpperCase()}
      </div>
      <div style={{
        padding: '6px 8px',
        fontSize: '0.73rem',
        lineHeight: 1.8,
      }}>
        {lines.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{
      padding: '12px 14px',
      background: 'var(--bg-secondary, #f9f9f9)',
      border: '1px solid var(--border-color, #e5e5e5)',
      borderRadius: 8,
      marginBottom: '1rem',
    }}>
      <div style={{
        fontSize: '0.72rem',
        fontWeight: 700,
        color: 'var(--text-secondary, #888)',
        marginBottom: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}>
        How variables connect nodes
      </div>
      <div className="wftut-varbind-row" style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'nowrap',
      }}>
        <NodeBox
          type="input"
          lines={[
            <span key="a">variable_name: {tag(C.input, 'query')}</span>,
            <span key="b" style={{ color: 'var(--text-secondary,#aaa)', fontStyle:'italic' }}>
              writes context
            </span>,
          ]}
        />
        <span className="wftut-varbind-arrow" style={{ color: 'var(--text-secondary,#aaa)', fontSize: '1rem' }}>→</span>
        <NodeBox
          type="agent"
          lines={[
            <span key="a">prompt: {tag(C.input, '{{query}}')}</span>,
            <span key="b">output_variable: {tag(C.agent, 'result')}</span>,
          ]}
        />
        <span className="wftut-varbind-arrow" style={{ color: 'var(--text-secondary,#aaa)', fontSize: '1rem' }}>→</span>
        <NodeBox
          type="output"
          lines={[
            <span key="a">variable_name: {tag(C.agent, 'result')}</span>,
            <span key="b" style={{ color: 'var(--text-secondary,#aaa)', fontStyle:'italic' }}>
              reads context
            </span>,
          ]}
        />
      </div>
      <div style={{
        marginTop: 10,
        fontSize: '0.76rem',
        color: 'var(--text-color)',
        lineHeight: 1.5,
      }}>
        The Agent's {tag(C.agent, 'output_variable')} and the
        Output's {tag(C.agent, 'variable_name')} are the{' '}
        <strong>same name</strong> — that's the link. If they don't
        match, the Output reads{' '}
        <code
          style={{
            background: '#fee2e2',
            color: '#dc2626',
            padding: '0 4px',
            borderRadius: 3,
          }}
        >
          null
        </code>.
      </div>
    </div>
  );
}

/* ─── Per-step auto-advance delays (ms) ─────────────────────── */

/*
 * STEP_DELAYS[i] = how long to wait at step i before moving to i+1.
 * Index 0 = idle; the array runs up to maxStep - 1.
 */
const STEP_DELAYS = [400, 1300, 1400, 1600, 1400, 1400, 1700, 2100, 0];
const MAX_STEP = 8;

/* ─── Main export ────────────────────────────────────────────── */

const WorkflowTutorialContent = ({ onClose }) => {
  useInjectCSS();
  useWfCSS();

  const [step, setStep]         = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef                = useRef(null);
  const demoScrollRef           = useRef(null);

  /* Clear the pending advance timer. */
  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /* Drive auto-advance: when playing and not at the end, schedule
   * the next step after the step-specific delay. */
  useEffect(() => {
    if (!isPlaying || step >= MAX_STEP) {
      if (step >= MAX_STEP) setIsPlaying(false);
      return;
    }
    const delay = STEP_DELAYS[step] ?? 1400;
    timerRef.current = setTimeout(() => {
      setStep((s) => s + 1);
    }, delay);
    return () => clearTimer();
  }, [isPlaying, step, clearTimer]);

  /* Clean up on unmount. */
  useEffect(() => clearTimer, [clearTimer]);

  const handlePlay = useCallback(() => {
    if (step >= MAX_STEP) {
      /* Replay from the beginning */
      setStep(0);
    }
    setIsPlaying(true);
  }, [step]);

  const handlePause = useCallback(() => {
    clearTimer();
    setIsPlaying(false);
  }, [clearTimer]);

  const handlePrev = useCallback(() => {
    clearTimer();
    setIsPlaying(false);
    setStep((s) => Math.max(0, s - 1));
  }, [clearTimer]);

  const handleNext = useCallback(() => {
    clearTimer();
    setIsPlaying(false);
    setStep((s) => Math.min(MAX_STEP, s + 1));
  }, [clearTimer]);

  const handleReset = useCallback(() => {
    clearTimer();
    setIsPlaying(false);
    setStep(0);
  }, [clearTimer]);

  const isDone  = step >= MAX_STEP;
  const isIdle  = step === 0;
  const maxStep = MAX_STEP;

  return (
    <div style={{ lineHeight: 1.6, fontSize: '0.95rem' }}>
      <h2 style={{
        borderBottom: '1px solid var(--panel-border, #ccc)',
        paddingBottom: '0.5rem',
        marginTop: 0,
      }}>
        Workflow Builder — How It Works
      </h2>
      <p style={{ marginBottom: '1.25rem', fontSize: '0.88rem', color: 'var(--text-color)' }}>
        Workflows chain AI agents and tools together. Run the simulation
        below, then read the reference guide.
      </p>

      {/* ── Simulation viewport ── */}
      <div className="wftut-viewport" style={{
        border: '1px solid var(--border-color, #ddd)',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: 'var(--bg-primary, #fff)',
        display: 'flex',
        flexDirection: 'column',
        height: '340px',
      }}>
        {/* Title bar */}
        <div style={{
          padding: '0.4rem 1rem',
          borderBottom: '1px solid var(--border-color, #ddd)',
          backgroundColor: 'var(--bg-secondary, #f5f5f5)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: '0.76rem',
          color: 'var(--text-secondary, #888)',
          userSelect: 'none',
        }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ff5f57', display: 'inline-block' }} />
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ffbd2e', display: 'inline-block' }} />
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#28c940', display: 'inline-block' }} />
          <span style={{ marginLeft: 6 }}>
            <RiFlowChart style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Workflow Builder — Input → Agent → Output
          </span>
        </div>

        {/* Demo area */}
        <div
          ref={demoScrollRef}
          className="wftut-demo-area"
          style={{ flex: 1, overflow: 'hidden' }}
        >
          <WorkflowDemoPanel step={step} />
        </div>

        {/* Controls bar — two fixed rows so buttons never shift position */}
        <div style={{
          padding: '0.5rem 1rem',
          borderTop: '1px solid var(--border-color, #ddd)',
          backgroundColor: 'var(--bg-secondary, #f5f5f5)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.4rem',
        }}>
          {/* Row 1: progress dots + status | Reset (right, always present) */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {Array.from({ length: maxStep }).map((_, i) => (
                <span
                  key={i}
                  onClick={() => { clearTimer(); setIsPlaying(false); setStep(i + 1); }}
                  title={`Jump to step ${i + 1}`}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: i < step ? C.agent : 'var(--border-color, #ddd)',
                    transition: 'background-color 0.3s',
                    cursor: 'pointer',
                  }}
                />
              ))}
              <span style={{
                marginLeft: 8,
                fontSize: '0.76rem',
                color: 'var(--text-secondary, #888)',
                whiteSpace: 'nowrap',
              }}>
                {isIdle
                  ? 'Ready'
                  : isPlaying
                    ? `Step ${step} / ${maxStep}`
                    : isDone
                      ? 'Complete'
                      : `Step ${step} / ${maxStep} — paused`}
              </span>
            </div>
            <button
              onClick={handleReset}
              disabled={isIdle}
              title="Reset to beginning"
              style={{
                display: 'flex', alignItems: 'center', gap: 3,
                padding: '0.28rem 0.6rem',
                fontSize: '0.78rem', fontWeight: 500,
                border: '1px solid var(--border-color, #ddd)',
                borderRadius: 6,
                background: 'transparent',
                color: 'var(--text-secondary, #666)',
                cursor: isIdle ? 'default' : 'pointer',
                opacity: isIdle ? 0.4 : 1,
              }}
            >
              <RiRefreshLine size={12} /> Reset
            </button>
          </div>

          {/* Row 2: ◀ · Play/Pause · ▶ — always three slots, no position shift */}
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', justifyContent: 'center' }}>
            {/* ◀ Prev — slot 1, always present */}
            <button
              onClick={handlePrev}
              disabled={step === 0}
              title="Previous step"
              style={{
                display: 'flex', alignItems: 'center',
                padding: '0.3rem 0.6rem',
                fontSize: '0.78rem',
                border: '1px solid var(--border-color, #ddd)',
                borderRadius: 6,
                background: 'transparent',
                color: step === 0 ? 'var(--text-secondary, #ccc)' : 'var(--text-color)',
                cursor: step === 0 ? 'not-allowed' : 'pointer',
                opacity: step === 0 ? 0.4 : 1,
              }}
            >
              <RiSkipBackLine size={14} />
            </button>

            {/* Centre button — slot 2, label/style change only, no position shift */}
            {isPlaying ? (
              <button
                onClick={handlePause}
                title="Pause"
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '0.3rem 0.85rem',
                  fontSize: '0.8rem', fontWeight: 700,
                  border: `1px solid ${C.agent}`,
                  borderRadius: 6,
                  background: 'transparent',
                  color: C.agent,
                  cursor: 'pointer',
                  minWidth: '5rem',
                }}
              >
                <RiPauseLine size={14} /> Pause
              </button>
            ) : (
              <button
                onClick={handlePlay}
                title={isDone ? 'Replay' : isIdle ? 'Start Tutorial' : 'Resume'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '0.3rem 0.85rem',
                  fontSize: '0.8rem', fontWeight: 700,
                  border: `1px solid ${C.agent}`,
                  borderRadius: 6,
                  background: C.agent,
                  color: '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  minWidth: '5rem',
                }}
              >
                <RiPlayCircleLine size={14} />
                {isDone ? 'Replay' : isIdle ? 'Run' : 'Resume'}
              </button>
            )}

            {/* ▶ Next — slot 3, always present */}
            <button
              onClick={handleNext}
              disabled={step >= MAX_STEP}
              title="Next step"
              style={{
                display: 'flex', alignItems: 'center',
                padding: '0.3rem 0.6rem',
                fontSize: '0.78rem',
                border: '1px solid var(--border-color, #ddd)',
                borderRadius: 6,
                background: 'transparent',
                color: step >= MAX_STEP ? 'var(--text-secondary, #ccc)' : 'var(--text-color)',
                cursor: step >= MAX_STEP ? 'not-allowed' : 'pointer',
                opacity: step >= MAX_STEP ? 0.4 : 1,
              }}
            >
              <RiSkipForwardLine size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Explainer ── */}
      <div style={{
        marginTop: '1.75rem',
        padding: '1rem 1.25rem',
        border: '1px solid var(--border-color, #ddd)',
        borderRadius: '12px',
        background: 'var(--bg-secondary, #fafafa)',
      }}>
        <div style={{
          fontWeight: 700,
          fontSize: '0.88rem',
          color: C.agent,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: '0.5rem',
        }}>
          <RiCodeSSlashLine size={16} /> Reference Guide
        </div>

        <SectionLabel>The variable system — the most important concept</SectionLabel>
        <VarBindingDiagram />

        <ExplainerRow
          Icon={RiCodeSSlashLine}
          iconColor={C.agent}
          label="{{variable_name}} syntax"
          description="Anywhere you see a text field in a node config, you can write {{name}} to inject the current value of that variable from the shared context. Used in Agent prompt templates and Tool parameters."
        />
        <ExplainerRow
          Icon={RiNodeTree}
          iconColor={C.logic}
          label="Edges define order, not data"
          description="Drawing an edge from A → B only tells the engine to run B after A finishes. The actual data passes via named variables. If B expects {{result}} but A wrote to agent_output, B gets nothing."
        />

        <SectionLabel>Node types</SectionLabel>
        <NodeCard
          type="input"
          description="Defines a named entry point. The value is supplied by the user each run via the input dialog."
          fields={[
            { name: 'variable_name', desc: 'the key written into context (e.g. query)' },
            { name: 'default_value', desc: 'pre-fills the run dialog' },
          ]}
        />
        <NodeCard
          type="agent"
          description="Runs an AI agent with a prompt you define. Upstream variables inject via {{name}} in the prompt template."
          fields={[
            { name: 'prompt_template', desc: 'use {{query}} to reference upstream values' },
            { name: 'output_variable', desc: 'the key this agent writes its response into' },
            { name: 'agent_type', desc: 'generalist · researcher · evaluator · challenger' },
          ]}
        />
        <NodeCard
          type="tool"
          description="Runs a search or utility tool. Parameters can reference context variables via {{name}}."
          fields={[
            { name: 'tool_type', desc: 'web_search · arxiv_search · tavily_search · …' },
            { name: 'parameters', desc: 'JSON; use {{var}} in values to inject context' },
            { name: 'output_variable', desc: 'the key this tool writes its result into' },
          ]}
        />
        <NodeCard
          type="output"
          description="Reads a final value from context and surfaces it as the workflow result. Must exist in the DAG."
          fields={[
            { name: 'variable_name', desc: 'must exactly match an upstream output_variable' },
          ]}
        />
        <NodeCard
          type="logic"
          description="Controls execution flow. Condition routes to different branches; Merge waits for all parallel branches."
          fields={[
            { name: 'logic_type', desc: 'condition · merge · set_variable' },
            { name: 'expression', desc: '(condition) e.g. score > 0.8' },
          ]}
        />

        <SectionLabel>Common mistakes</SectionLabel>
        <div style={{
          fontSize: '0.82rem',
          color: 'var(--text-color)',
          lineHeight: 1.6,
        }}>
          <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
            <span style={{ color: '#dc2626', fontWeight: 700, flexShrink: 0 }}>✗</span>
            <span>
              <strong>output_variable / variable_name mismatch</strong> — Agent
              defaults to <code style={{ background: '#f1f5f9', color: '#1e293b', padding: '1px 4px', borderRadius: 3 }}>agent_output</code>,
              but the Output node expects <code style={{ background: '#f1f5f9', color: '#1e293b', padding: '1px 4px', borderRadius: 3 }}>result</code>.
              The result is <code style={{ background: '#fee2e2', color: '#dc2626', padding: '1px 4px', borderRadius: 3 }}>null</code>.
              Fix: set the Agent's output_variable to match the Output's variable_name — or change the Output's variable_name to match.
            </span>
          </div>
          <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
            <span style={{ color: '#dc2626', fontWeight: 700, flexShrink: 0 }}>✗</span>
            <span>
              <strong>Stray edge bypassing processing nodes</strong> — an
              accidental edge from Input → Output skips Agent and Tool entirely.
              Check your canvas for unintended connections and delete them with
              the Delete key or by clicking the edge.
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: '#dc2626', fontWeight: 700, flexShrink: 0 }}>✗</span>
            <span>
              <strong>Tool output not forwarded</strong> — a Tool node's result
              (default: <code style={{ background: '#f1f5f9', color: '#1e293b', padding: '1px 4px', borderRadius: 3 }}>tool_output</code>)
              is only useful if a downstream Agent reads{' '}
              <code style={{ background: '#f1f5f9', color: '#1e293b', padding: '1px 4px', borderRadius: 3 }}>{'{{tool_output}}'}</code>
              in its prompt template.
            </span>
          </div>
        </div>
      </div>

      <p style={{
        marginTop: '1.1rem',
        fontSize: '0.83rem',
        color: 'var(--text-secondary, #888)',
        fontStyle: 'italic',
      }}>
        Start simple: Input → Agent (output_variable = result) → Output
        (variable_name = result). Add Tool and Logic nodes once the
        basic chain works.
      </p>
    </div>
  );
};

export default WorkflowTutorialContent;
